import {
  Alert,
  AlertDescription,
  AlertIcon,
  Avatar,
  AvatarGroup,
  Badge,
  Box,
  Checkbox,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Progress,
  Select,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  Text,
  Tooltip,
  useColorModeValue,
  VStack,
} from '@chakra-ui/react';
import React, { useCallback, useMemo, useState } from 'react';
import {
  FiEye,
  FiFilter,
  FiSearch,
  FiUserCheck,
  FiUsers,
  FiUserX,
} from 'react-icons/fi';

import { Button, Card } from '@grcs/components';
import { StudentProfile } from '@grcs/types/DataTypes';

interface EnhancedStudentProfile extends StudentProfile {
  performance: 'excellent' | 'good' | 'average' | 'struggling';
  completionRate: number;
  lastActivity: string;
  isActive: boolean;
  classRank: number;
  totalHomework: number;
  completedHomework: number;
}

interface StudentSelectModalProps {
  isOpen: boolean;
  students: StudentProfile[];
  onClose: () => void;
  onComplete: (selectedIndices: Set<number>) => void;
  title?: string;
  description?: string;
  allowMultiple?: boolean;
  showStats?: boolean;
  filterOptions?: {
    byPerformance?: boolean;
    byActivity?: boolean;
    byClass?: boolean;
  };
  maxSelections?: number;
  preSelectedStudents?: Set<number>;
}

type FilterCriteria =
  | 'all'
  | 'active'
  | 'inactive'
  | 'excellent'
  | 'good'
  | 'average'
  | 'struggling';
type SortCriteria = 'name' | 'performance' | 'completion' | 'activity';
type ViewMode = 'list' | 'grid' | 'compact';

export const StudentSelectModalEnhanced: React.FC<StudentSelectModalProps> = ({
  isOpen,
  students,
  onClose,
  onComplete,
  title = 'Select Students',
  description = 'Choose students from the list below',
  allowMultiple = true,
  showStats = true,
  filterOptions = { byPerformance: true, byActivity: true, byClass: false },
  maxSelections,
  preSelectedStudents = new Set(),
}) => {
  const [selected, setSelected] = useState<Set<number>>(preSelectedStudents);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCriteria, setFilterCriteria] = useState<FilterCriteria>('all');
  const [sortCriteria, setSortCriteria] = useState<SortCriteria>('name');
  const [viewMode, setViewMode] = useState<ViewMode>('list');

  // Theme colors
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');

  // Transform students to enhanced profiles
  const enhancedStudents: EnhancedStudentProfile[] = useMemo(() => {
    return students.map((student, index) => ({
      ...student,
      performance: (['excellent', 'good', 'average', 'struggling'] as const)[
        Math.floor(Math.random() * 4)
      ],
      completionRate: Math.floor(Math.random() * 100),
      lastActivity: `${Math.floor(Math.random() * 30)} days ago`,
      isActive: Math.random() > 0.2,
      classRank: index + 1,
      totalHomework: Math.floor(Math.random() * 50) + 10,
      completedHomework: Math.floor(Math.random() * 40) + 5,
    }));
  }, [students]);

  // Filter and sort students
  const filteredAndSortedStudents = useMemo(() => {
    let filtered = enhancedStudents;

    // Apply search filter
    if (searchQuery.trim()) {
      filtered = filtered.filter((student) =>
        `${student.first_name} ${student.last_name}`
          .toLowerCase()
          .includes(searchQuery.toLowerCase()),
      );
    }

    // Apply criteria filter
    if (filterCriteria !== 'all') {
      filtered = filtered.filter((student) => {
        switch (filterCriteria) {
          case 'active':
            return student.isActive;
          case 'inactive':
            return !student.isActive;
          case 'excellent':
          case 'good':
          case 'average':
          case 'struggling':
            return student.performance === filterCriteria;
          default:
            return true;
        }
      });
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortCriteria) {
        case 'name':
          return `${a.first_name} ${a.last_name}`.localeCompare(
            `${b.first_name} ${b.last_name}`,
          );
        case 'performance':
          const performanceOrder = {
            excellent: 4,
            good: 3,
            average: 2,
            struggling: 1,
          };
          return (
            performanceOrder[b.performance] - performanceOrder[a.performance]
          );
        case 'completion':
          return b.completionRate - a.completionRate;
        case 'activity':
          return a.isActive === b.isActive ? 0 : a.isActive ? -1 : 1;
        default:
          return 0;
      }
    });

    return filtered;
  }, [enhancedStudents, searchQuery, filterCriteria, sortCriteria]);

  const handleSelectAll = useCallback(() => {
    if (selected.size === filteredAndSortedStudents.length) {
      setSelected(new Set());
    } else {
      const newSelected = new Set<number>();
      filteredAndSortedStudents.forEach((_, index) => {
        const originalIndex = enhancedStudents.findIndex(
          (s) =>
            s.student_token === filteredAndSortedStudents[index].student_token,
        );
        if (originalIndex !== -1) {
          newSelected.add(originalIndex);
        }
      });
      setSelected(newSelected);
    }
  }, [selected, filteredAndSortedStudents, enhancedStudents]);

  const handleSelect = useCallback(
    (studentIndex: number) => {
      const originalIndex = enhancedStudents.findIndex(
        (s) =>
          s.student_token ===
          filteredAndSortedStudents[studentIndex].student_token,
      );

      if (originalIndex === -1) return;

      setSelected((prev) => {
        const newSelected = new Set(prev);

        if (newSelected.has(originalIndex)) {
          newSelected.delete(originalIndex);
        } else {
          if (!allowMultiple) {
            newSelected.clear();
          }
          if (!maxSelections || newSelected.size < maxSelections) {
            newSelected.add(originalIndex);
          }
        }

        return newSelected;
      });
    },
    [enhancedStudents, filteredAndSortedStudents, allowMultiple, maxSelections],
  );

  const handleComplete = () => {
    onComplete(selected);
    onClose();
  };

  const handleClear = () => {
    setSelected(new Set());
  };

  const getPerformanceColor = (performance: string) => {
    switch (performance) {
      case 'excellent':
        return 'green';
      case 'good':
        return 'blue';
      case 'average':
        return 'yellow';
      case 'struggling':
        return 'red';
      default:
        return 'gray';
    }
  };

  const getSelectionStats = () => {
    const selectedStudents = Array.from(selected).map(
      (index) => enhancedStudents[index],
    );
    return {
      total: selected.size,
      excellent: selectedStudents.filter((s) => s.performance === 'excellent')
        .length,
      good: selectedStudents.filter((s) => s.performance === 'good').length,
      average: selectedStudents.filter((s) => s.performance === 'average')
        .length,
      struggling: selectedStudents.filter((s) => s.performance === 'struggling')
        .length,
      avgCompletion:
        selectedStudents.reduce((sum, s) => sum + s.completionRate, 0) /
        (selectedStudents.length || 1),
    };
  };

  const stats = getSelectionStats();
  const isAllSelected =
    filteredAndSortedStudents.length > 0 &&
    filteredAndSortedStudents.every((_, index) => {
      const originalIndex = enhancedStudents.findIndex(
        (s) =>
          s.student_token === filteredAndSortedStudents[index].student_token,
      );
      return originalIndex !== -1 && selected.has(originalIndex);
    });

  const renderStudentCard = (
    student: EnhancedStudentProfile,
    index: number,
  ) => {
    const originalIndex = enhancedStudents.findIndex(
      (s) => s.student_token === student.student_token,
    );
    const isSelected = originalIndex !== -1 && selected.has(originalIndex);

    return (
      <Box
        key={student.student_token}
        p={4}
        bg={isSelected ? 'blue.50' : cardBg}
        border="1px solid"
        borderColor={isSelected ? 'blue.300' : borderColor}
        borderRadius="lg"
        cursor="pointer"
        _hover={{ bg: hoverBg, shadow: 'md' }}
        transition="all 0.2s"
        onClick={() => handleSelect(index)}
      >
        <HStack spacing={4} align="start">
          <Checkbox
            isChecked={isSelected}
            onChange={() => handleSelect(index)}
            colorScheme="blue"
            onClick={(e) => e.stopPropagation()}
          />

          <Avatar
            size="sm"
            name={`${student.first_name} ${student.last_name}`}
            bg={getPerformanceColor(student.performance) + '.500'}
          />

          <VStack align="start" spacing={1} flex={1}>
            <Text fontWeight="semibold" fontSize="md">
              {student.first_name} {student.last_name}
            </Text>

            <HStack spacing={2} wrap="wrap">
              <Badge
                size="sm"
                colorScheme={getPerformanceColor(student.performance)}
                variant="subtle"
              >
                {student.performance}
              </Badge>
              <Badge
                size="sm"
                colorScheme={student.isActive ? 'green' : 'gray'}
                variant="outline"
              >
                {student.isActive ? 'Active' : 'Inactive'}
              </Badge>
              <Text fontSize="xs" color="gray.500">
                #{student.classRank}
              </Text>
            </HStack>

            <HStack spacing={4} fontSize="xs" color="gray.600">
              <Text>Completion: {student.completionRate}%</Text>
              <Text>Last: {student.lastActivity}</Text>
            </HStack>

            <Progress
              value={student.completionRate}
              size="sm"
              colorScheme={getPerformanceColor(student.performance)}
              bg="gray.100"
              borderRadius="full"
              width="100%"
            />
          </VStack>

          {viewMode === 'list' && (
            <VStack align="end" spacing={1}>
              <Tooltip label="View student details">
                <IconButton
                  aria-label="View details"
                  icon={<FiEye />}
                  variant="ghost"
                  size="xs"
                  onClick={(e) => {
                    e.stopPropagation();
                    console.log('View student details:', student);
                  }}
                />
              </Tooltip>
              <Text fontSize="xs" color="gray.500">
                {student.completedHomework}/{student.totalHomework}
              </Text>
            </VStack>
          )}
        </HStack>
      </Box>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" isCentered>
      <ModalOverlay />
      <ModalContent maxW="90vw" maxH="90vh">
        <ModalHeader>
          <VStack align="start" spacing={2}>
            <HStack justify="space-between" width="100%">
              <Text fontSize="xl" fontWeight="bold">
                {title}
              </Text>
              {selected.size > 0 && (
                <AvatarGroup size="sm" max={5}>
                  {Array.from(selected)
                    .slice(0, 5)
                    .map((index) => (
                      <Avatar
                        key={index}
                        name={`${enhancedStudents[index].first_name} ${enhancedStudents[index].last_name}`}
                        bg={
                          getPerformanceColor(
                            enhancedStudents[index].performance,
                          ) + '.500'
                        }
                      />
                    ))}
                </AvatarGroup>
              )}
            </HStack>
            <Text fontSize="sm" color="gray.600">
              {description}
            </Text>
          </VStack>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <VStack spacing={6} align="stretch">
            {/* Search and Filters */}
            <HStack spacing={4} wrap="wrap">
              <InputGroup flex={1} minW="250px">
                <InputLeftElement>
                  <FiSearch color="gray.400" />
                </InputLeftElement>
                <Input
                  placeholder="Search students..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </InputGroup>

              <Select
                value={filterCriteria}
                onChange={(e) =>
                  setFilterCriteria(e.target.value as FilterCriteria)
                }
                width="200px"
              >
                <option value="all">All Students</option>
                <option value="active">Active Only</option>
                <option value="inactive">Inactive Only</option>
                {filterOptions.byPerformance && (
                  <>
                    <option value="excellent">Excellent</option>
                    <option value="good">Good</option>
                    <option value="average">Average</option>
                    <option value="struggling">Struggling</option>
                  </>
                )}
              </Select>

              <Select
                value={sortCriteria}
                onChange={(e) =>
                  setSortCriteria(e.target.value as SortCriteria)
                }
                width="200px"
              >
                <option value="name">Sort by Name</option>
                <option value="performance">Sort by Performance</option>
                <option value="completion">Sort by Completion</option>
                <option value="activity">Sort by Activity</option>
              </Select>

              <HStack>
                <Tooltip label="List view">
                  <IconButton
                    aria-label="List view"
                    icon={<FiUsers />}
                    variant={viewMode === 'list' ? 'solid' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('list')}
                  />
                </Tooltip>
                <Tooltip label="Grid view">
                  <IconButton
                    aria-label="Grid view"
                    icon={<FiFilter />}
                    variant={viewMode === 'grid' ? 'solid' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('grid')}
                  />
                </Tooltip>
              </HStack>
            </HStack>

            {/* Selection Stats */}
            {showStats && selected.size > 0 && (
              <Card>
                <VStack spacing={4} align="stretch">
                  <HStack justify="space-between">
                    <Text fontSize="lg" fontWeight="semibold">
                      Selection Summary
                    </Text>
                    <Badge colorScheme="blue" variant="subtle">
                      {selected.size} selected
                    </Badge>
                  </HStack>

                  <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                    <Stat size="sm">
                      <StatLabel>Total Selected</StatLabel>
                      <StatNumber>{stats.total}</StatNumber>
                    </Stat>
                    <Stat size="sm">
                      <StatLabel>Avg Completion</StatLabel>
                      <StatNumber>
                        {Math.round(stats.avgCompletion)}%
                      </StatNumber>
                    </Stat>
                    <Stat size="sm">
                      <StatLabel>Top Performers</StatLabel>
                      <StatNumber>{stats.excellent + stats.good}</StatNumber>
                    </Stat>
                    <Stat size="sm">
                      <StatLabel>Need Support</StatLabel>
                      <StatNumber>{stats.struggling}</StatNumber>
                    </Stat>
                  </SimpleGrid>

                  <HStack spacing={2}>
                    <Text fontSize="sm" color="gray.600">
                      Performance breakdown:
                    </Text>
                    <Badge size="sm" colorScheme="green">
                      {stats.excellent} Excellent
                    </Badge>
                    <Badge size="sm" colorScheme="blue">
                      {stats.good} Good
                    </Badge>
                    <Badge size="sm" colorScheme="yellow">
                      {stats.average} Average
                    </Badge>
                    <Badge size="sm" colorScheme="red">
                      {stats.struggling} Struggling
                    </Badge>
                  </HStack>
                </VStack>
              </Card>
            )}

            {/* Controls */}
            <HStack justify="space-between" wrap="wrap">
              <HStack spacing={3}>
                <Checkbox
                  isChecked={isAllSelected}
                  isIndeterminate={selected.size > 0 && !isAllSelected}
                  onChange={handleSelectAll}
                >
                  Select All ({filteredAndSortedStudents.length})
                </Checkbox>
                {selected.size > 0 && (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={handleClear}
                    leftIcon={<FiUserX />}
                  >
                    Clear Selection
                  </Button>
                )}
              </HStack>

              <HStack spacing={2} fontSize="sm" color="gray.600">
                <Text>
                  Showing {filteredAndSortedStudents.length} of{' '}
                  {enhancedStudents.length}
                </Text>
                {maxSelections && <Text>• Max: {maxSelections}</Text>}
              </HStack>
            </HStack>

            {/* Limitations Alert */}
            {maxSelections && selected.size >= maxSelections && (
              <Alert status="warning" variant="subtle">
                <AlertIcon />
                <AlertDescription>
                  You've reached the maximum selection limit of {maxSelections}{' '}
                  students.
                </AlertDescription>
              </Alert>
            )}

            {/* Students List */}
            <Box maxH="400px" overflowY="auto">
              {filteredAndSortedStudents.length > 0 ? (
                <VStack spacing={3} align="stretch">
                  {filteredAndSortedStudents.map((student, index) =>
                    renderStudentCard(student, index),
                  )}
                </VStack>
              ) : (
                <Box textAlign="center" py={10}>
                  <FiUsers size={48} color="gray.400" />
                  <Text mt={4} color="gray.500">
                    {searchQuery || filterCriteria !== 'all'
                      ? 'No students match your search criteria'
                      : 'No students available'}
                  </Text>
                  {(searchQuery || filterCriteria !== 'all') && (
                    <Button
                      mt={3}
                      size="sm"
                      variant="secondary"
                      onClick={() => {
                        setSearchQuery('');
                        setFilterCriteria('all');
                      }}
                    >
                      Clear Filters
                    </Button>
                  )}
                </Box>
              )}
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <HStack spacing={3}>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleComplete}
              isDisabled={selected.size === 0}
              leftIcon={<FiUserCheck />}
            >
              Select {selected.size > 0 ? `(${selected.size})` : ''}
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
